import React from 'react';
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

export default function ConfirmModal({ isOpen, onConfirm, onCancel, title, message, confirmText, confirmButtonClass, hideCancel = false, cancelText = 'Cancel', cancelButtonClass = 'text-gray-700 bg-gray-100 hover:bg-gray-200' }) {
  const modalRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        onConfirm(); // Always trigger discard when Enter is pressed in modal
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onCancel(); // Allow Escape to cancel
      }
    };

    if (isOpen) {
      // Set focus on modal when it opens
      if (modalRef.current) {
        modalRef.current.focus();
      }
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onConfirm, onCancel]);

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 bg-black bg-opacity-40 z-[2700] flex items-center justify-center p-3 md:p-4" style={{ zIndex: 2700 }}>
      <div 
        ref={modalRef}
        tabIndex={-1}
        className="bg-white p-3 md:p-6 rounded-lg w-full max-w-[88vw] md:max-w-[400px] shadow-xl outline-none relative z-[2701]"
        style={{ zIndex: 2701 }}
      >
        <h3 className="text-base md:text-lg font-semibold mb-3 md:mb-4">{title}</h3>
        {message && <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6">{message}</p>}
        <div className="flex justify-end gap-2 md:gap-3">
          {!hideCancel && (
            <button
              onClick={onCancel}
              className={`px-4 py-2.5 md:py-2 text-sm md:text-base rounded touch-manipulation min-h-[44px] md:min-h-0 ${cancelButtonClass}`}
            >
              {cancelText}
            </button>
          )}
          <button
            onClick={onConfirm}
            className={`px-4 py-2.5 md:py-2 text-sm md:text-base text-white rounded touch-manipulation min-h-[44px] md:min-h-0 ${confirmButtonClass}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );

  // Use portal to render outside EventFormModal's DOM structure
  return createPortal(modalContent, document.body);
}
